import React, { useState } from 'react';
import { entityLabel } from './entityLabels';
import RunCompositionBar, { RUN_BANDS } from './RunCompositionBar';
import DataQualityDeltaBadge from '../DataQualityDeltaBadge';
import { NEST_SHELL } from './QualityOverview';

// Legend mirrors the Quality tab's swatch row so the two views read as one system.
const LEGEND_ORDER = ['carried', 'added', 'missing', 'exhausted', 'source_owned'];
function Legend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-gray-600 pb-3">
      {LEGEND_ORDER.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5" title={RUN_BANDS[k].hint}>
          <span className="inline-block w-2.5 h-2.5 rounded-sm flex-none"
            style={{ background: RUN_BANDS[k].c, border: k === 'missing' ? '1px solid #c3c2b7' : 'none' }} />
          {RUN_BANDS[k].label}
        </span>
      ))}
    </div>
  );
}

// The selected run's per-entity → per-field breakdown, each drawn as a composition bar
// (carried · added-this-run · can't-enrich · missing). Replaces the old numbers-only
// RunFieldDeltas. Entities collapse; unchanged fields hide until "Show all fields".
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const pct = (n) => `${Math.round(Number(n || 0))}%`;


// A run's contribution splits `filled` into what earlier runs carried vs what this run
// added; `exhausted` (can't-enrich) and `source_owned` (not-enriched, from the ATS) are
// each carved out of the gap.
const barProps = (x) => ({
  carried: Math.max(0, Number(x.filled || 0) - Number(x.values_added || 0)),
  addedThisRun: Math.max(0, Number(x.values_added || 0)),
  exhausted: Number(x.exhausted || 0),
  sourceOwned: Number(x.source_owned || 0),
  total: Number(x.total || 0)
});

function ChangeNums({ x }) {
  return (
    <span className="flex-none flex items-center gap-2 text-xs tabular-nums text-gray-600 whitespace-nowrap">
      {pct(x.before_pct)} <span className="text-gray-300">→</span> {pct(x.after_pct)}
      <DataQualityDeltaBadge value={x.delta_pct} />
      <span className="text-emerald-600 w-14 text-right">{x.values_added > 0 ? `+${fmt(x.values_added)}` : '—'}</span>
    </span>
  );
}

const fieldLabel = (name) => String(name || '').replace(/_/g, ' ');

function FieldRow({ f }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
      <span className="w-28 flex-none text-sm text-gray-800">{fieldLabel(f.field)}</span>
      <span className="flex-1 min-w-0"><RunCompositionBar {...barProps(f)} height={14} /></span>
      <ChangeNums x={f} />
    </div>
  );
}

export default function RunEntityBreakdown({ entities = [], baseline }) {
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(() => new Set(entities.length ? [entities[0].entity] : []));
  const toggle = (name) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  if (!entities.length) return null;

  return (
    <div>
      <Legend />
      {entities.map((ent) => {
        const fields = ent.fields || [];
        const changed = fields.filter((f) => Math.abs(f.delta_pct) >= 0.5 || f.values_added !== 0);
        const shown = showAll ? fields : changed;
        const isOpen = open.has(ent.entity);
        return (
          <div key={ent.entity} className="border-t border-gray-100 first:border-0 py-1">
            <button type="button" onClick={() => toggle(ent.entity)} aria-expanded={isOpen}
              className="w-full flex items-center gap-3 py-2.5 px-1 rounded-lg hover:bg-gray-50 text-left">
              <svg className={`w-3.5 h-3.5 flex-none text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="flex-none w-32 text-sm font-semibold text-gray-900">{entityLabel(ent.entity)}</span>
              <span className="flex-1 min-w-0"><RunCompositionBar {...barProps(ent)} height={22} /></span>
              <ChangeNums x={ent} />
            </button>
            {isOpen && (
              <div className={`${NEST_SHELL} pl-4 pr-2 pt-1 pb-1`}>
                {shown.length ? shown.map((f) => <FieldRow key={f.field} f={f} />)
                  : <p className="text-xs text-gray-400 py-2">No field changes in this run.</p>}
              </div>
            )}
          </div>
        );
      })}
      <button type="button" onClick={() => setShowAll((v) => !v)} className="text-xs text-[#007A6B] hover:underline mt-2">
        {showAll ? 'Show changed fields only' : 'Show all fields'}
      </button>
    </div>
  );
}
